<template>
    <div class="container">
        <v-title>
            {{isFormUpdating ? 'Update' : 'Add'}} Segment
            <div slot="actions">
                <v-cancel :default-route="{name: 'segments'}"></v-cancel>
                <r-button
                    type="primary"
                    @click="save"
                    :disabled="isLoading">Save Segment</r-button>
            </div>
        </v-title>
        <v-error-messages :has-frontend-errors="$v.segment.$error || hasInvalidQuery" :server-error="serverError" class="stack-l">
            <li class="list-item" v-if="!$v.segment.data.name.required">Segment title is required.</li>
            <li class="list-item" v-if="hasInvalidQuery">Filters values are invalid.</li>
        </v-error-messages>
        <v-form-tile>
            <h2 slot="title">Define</h2>
            <p slot="description" class="text-muted stack-m">Your segment data will be defined for either the customer detail page or the lists page, and be composed of a specific type of data.</p>
            <p slot="description" class="text-muted">Your segment can be private (not accessible by any of your teammates) or else it would be accessible by any of your teammates.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <div class="field" :class="{'is-error': false}">
                                <label class="field-label">Page</label>
                                <div class="field-control has-icon-right">
                                    <select class="field-input" v-model="segment.data.page">
                                        <option
                                            v-for="page in segmentPages"
                                            :value="page.value">{{page.label}}
                                        </option>
                                    </select>
                                    <v-icon icon="caret-down"/>
                                </div>
                            </div>
                        </div>
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <div class="field" :class="{'is-error': false}">
                                <label class="field-label">Type</label>
                                <div class="field-control has-icon-right">
                                    <select class="field-input" v-model="segment.data.type" @change="updateType">
                                        <option
                                            v-for="type in segmentTypes"
                                            :value="type.value">{{type.label}}
                                        </option>
                                    </select>
                                    <v-icon icon="caret-down"/>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Share</h2>
            <p slot="description" class="text-muted">You can share your segment with other users if you want.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-full">
                            <segment-shared-with
                                ref="segmentSharedWith"
                                @update="updateSegmentSharedWith"
                                :value="segment"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Title</h2>
            <p slot="description" class="text-muted">Customize your segment title description.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <r-input
                                label="Title"
                                :help-text="`${maxTitleLength} characters maximum`"
                                :max-length="maxTitleLength"
                                v-model="segment.data.name"
                                :validate="$v.segment.data.name"/>
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Filters</h2>
            <p slot="description" class="text-muted">Define filters to only include the data you want within your segment.</p>
            <div slot="content" class="tile">
                <div class="tile-content tile-content-filter position-relative">
                    <div v-if="!isFiltersReady" class="inset-default">
                        <v-inline-loader/>
                    </div>
                    <query
                        :key="segment.data.type"
                        @ready="filtersReady"
                        @update="updateFilters"
                        @validation="queryValidated"
                        compact
                        use-separators
                        :query="filterQuery"
                        :filter-set="filterSet"/>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Columns</h2>
            <p slot="description" class="text-muted">Choose the columns you want in your segment.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-full">
                            <p class="text-muted stack-m">Drag and drop the columns to change the display order.</p>
                            <draggable
                                v-model="draggableColumns"
                                v-bind="draggableOptions"
                                @end="setFocus"
                                tag="ol"
                                class="list list-group list-divided stack-l">
                                <li
                                    v-for="(field, index) in draggableColumns"
                                    :data-index="index"
                                    :field-value="field.value"
                                    :key="`${field}-${index}`"
                                    class="list-item cursor-pointer align-items-center">
                                    <span class="no-flex inline-s drag-handle">
                                        <v-icon-button
                                            type="basic"
                                            class="cursor-move"
                                            size="small">
                                            <v-icon icon="drag"/>
                                        </v-icon-button>
                                    </span>
                                    <span class="list-item-content inline-l">
                                        <span class="display-flex align-items-center">
                                            <span>{{(index + 1)}}.&nbsp;{{field.label}}</span>
                                        </span>
                                    </span>
                                    <span class="no-flex">
                                        <v-icon-button
                                            :disabled="isColumnDisabled(field)"
                                            type="basic"
                                            size="small"
                                            @click.prevent.stop="() => deleteField(index)">
                                        <v-icon icon="trash"/>
                                    </v-icon-button>
                                    </span>
                                </li>
                            </draggable>
                            <r-button type="link" @click="showColumnsListModal = true">More Columns</r-button>
                            <columns-list-modal
                                :is-visible="showColumnsListModal"
                                :available-columns="availableColumns"
                                :default-columns="defaultColumns"
                                :columns="draggableColumns"
                                @update="updateColumns"
                                @close="closeColumnsListModal"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <v-form-tile>
            <h2 slot="title">Default Sort</h2>
            <p slot="description" class="text-muted">Pick the column you wish to be sorting on by default as well as if the sort should be ascending or descending.</p>
            <div slot="content" class="tile">
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-half grid-item-break-half">
                            <v-radio
                                label="Ascending"
                                group="sorting"
                                :model="columnSorting"
                                value=""
                                @update="updateSorting"
                                class="stack-m"/>
                            <v-radio
                                label="Descending"
                                group="sorting"
                                :model="columnSorting"
                                @update="updateSorting"
                                value="-"/>
                        </div>
                    </div>
                </div>
                <div class="tile-content">
                    <div class="grid-content">
                        <div class="grid-item grid-item-full">
                            <v-select
                                label="Select Column"
                                :options="sortableColumns"
                                :preselect-first="true"
                                v-model="selectedSortableColumn"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </v-form-tile>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <v-cancel :default-route="{name: 'segments'}"></v-cancel>
                <r-button type="primary" :loading="isLoading" @click="save">Save Segment</r-button>
            </div>
        </div>
    </div>
</template>
<script>
    import vTitle from '@/components/v-title';
    import {
        vFormTile,
        vCancel,
        vIconButton,
        vIcon,
        vRadio,
        vSelect,
        vErrorMessages,
    } from '@/components/form';
    import vInlineLoader from '@/components/v-inline-loader';
    import columnsListModal from './components/columns-list-modal';
    import segmentSharedWith from './components/segment-shared-with';
    import draggable from 'vuedraggable';
    import draggableDefaults from '@/vendor/draggable/defaults';
    import {required} from 'vuelidate/lib/validators';
    import transactionsFilters from '@/common/query/filter/transaction-filters';
    import invoicesFilters from '@/common/query/filter/invoice-filters';
    import ordersFilters from '@/common/query/filter/order-filters';
    import couponFilters from '@/common/query/filter/coupon-filters';
    import customersFilters from '@/common/query/filter/customer-filters';
    import apiLogsFilters from '@/common/query/filter/api-logs-filters';
    import webhooksLogsFilters from '@/common/query/filter/webhook-tracking-logs-filters';
    import transactionColumns from '@/modules/transactions/transaction-columns';
    import invoiceColumns from '@/modules/invoices/invoice-columns';
    import ordersColumns from '@/modules/orders/order-columns';
    import customerColumns from '@/modules/customers/customer-columns';
    import couponColumns from '@/modules/coupons/coupon-columns';
    import apiLogsColumns from '@/modules/api-keys/api-logs-columns';
    import webhookColumns from '@/modules/webhooks/webhook-tracking-logs-columns';
    import SegmentDataModel from '@/common/models/segment-data-model';
    import SegmentModel from '@/common/models/segment-model';
    import FilterSet from '@/components/query/filter-set';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import {mapActions} from 'vuex';

    const typeMapping = {
        [SegmentDataModel.Types.invoices]: {
            columns: invoiceColumns,
            filter: invoicesFilters,
        },
        [SegmentDataModel.Types.customers]: {
            columns: customerColumns,
            filter: customersFilters,
        },
        [SegmentDataModel.Types.transactions]: {
            columns: transactionColumns,
            filter: transactionsFilters,
        },
        [SegmentDataModel.Types.orders]: {
            columns: ordersColumns,
            filter: ordersFilters,
        },
        [SegmentDataModel.Types.couponRedemptions]: {
            columns: couponColumns,
            filter: couponFilters,
        },
        [SegmentDataModel.Types.apiLogs]: {
            columns: apiLogsColumns(),
            filter: apiLogsFilters,
        },
        [SegmentDataModel.Types.webhookLogs]: {
            columns: webhookColumns(),
            filter: webhooksLogsFilters,
        },
    };

    export default {
        name: 'segment-form',
        mixins: [FormLoaderMixin],
        props: {
            id: String,
            page: String,
        },
        components: {
            vTitle,
            vFormTile,
            vCancel,
            vIcon,
            vRadio,
            vSelect,
            vIconButton,
            vErrorMessages,
            columnsListModal,
            draggable,
            segmentSharedWith,
            vInlineLoader,
            Query: () => import('@/components/query2/query2'),
        },
        computed: {
            segmentPages() {
                return SegmentDataModel.getPageOptions();
            },
            segmentTypes() {
                return SegmentDataModel.getTypeOptions(this.segment.data.page);
            },
            filterSet() {
                const {type} = this.segment.data;
                return new FilterSet({store: this.$store, ...typeMapping[type].filter});
            },
        },
        data() {
            const segment = new SegmentModel({
                data: {
                    page: this.page,
                },
            });
            const {defaultColumns, availableColumns} = this.getColumnsForType(segment.data.type);
            const sortableColumns = this.getSortableColumns(availableColumns);
            return {
                isLoading: false,
                segment,
                draggableColumns: [],
                availableColumns,
                defaultColumns,
                sortableColumns,
                selectedSortableColumn: null,
                filterQuery: null,
                columnSorting: '',
                isFiltersReady: false,
                showColumnsListModal: false,
                maxTitleLength: 25,
                hasInvalidQuery: false,
                draggableOptions: draggableDefaults,
            };
        },
        validations() {
            return {
                segment: {
                    data: {
                        name: {required},
                    },
                },
            };
        },
        methods: {
            ...mapActions({
                addSegment: 'segments/add',
                updateSegment: 'segments/update',
                fetchUsers: 'users/fetch',
            }),
            updateFilters(data) {
                this.segment.data.filters = data;
            },
            updateType() {
                const {type} = this.segment.data;
                this.isFiltersReady = false;
                this.filterQuery = null;
                this.segment.data.filters = null;
                ({
                    defaultColumns: this.defaultColumns,
                    availableColumns: this.availableColumns,
                } = this.getColumnsForType(type));
                this.draggableColumns = [...this.defaultColumns];
                this.sortableColumns = this.getSortableColumns(this.availableColumns);
                this.selectedSortableColumn = this.sortableColumns[0].value;
            },
            filtersReady() {
                this.isFiltersReady = true;
            },
            isColumnDisabled(column) {
                const sortableColumns = this.draggableColumns.filter(item => item.sort);
                if (sortableColumns.length === 1 && column.sort) {
                    return true;
                }
                return this.draggableColumns.length === 1;
            },
            getColumnsForType(type) {
                const {columns} = typeMapping[type];
                const [defaultColumns, otherColumns] = ['defaultColumns', 'otherColumns'].reduce((result, prop) => {
                    result.push(Object.keys(columns[prop])
                        .map(item => (
                            {
                                value: columns[prop][item].id,
                                label: columns[prop][item].header,
                                sort: columns[prop][item].sortable,
                            })));
                    return result;
                }, []);
                return {
                    defaultColumns,
                    availableColumns: [].concat(defaultColumns, otherColumns),
                };
            },
            getSortableColumns(columns) {
                if (this.draggableColumns && this.draggableColumns.length) {
                    return columns.filter(item => item.sort && this.draggableColumns.find(column => column.value === item.value));
                }
                return columns.filter(item => item.sort);
            },
            async fetchData() {
                if (this.$auth.users.read.isAllowed) {
                    await this.fetchUsers();
                }
                this.draggableColumns = [...this.defaultColumns];
                this.sortableColumns = this.getSortableColumns(this.availableColumns);
            },
            async save() {
                this.$v.$touch();
                if (this.$v.segment.$error || this.hasInvalidQuery) {
                    this.scrollTop();
                    return;
                }
                this.segment.data.sort = `${this.columnSorting}${this.selectedSortableColumn}`;
                this.segment.data.columns = this.draggableColumns.map(column => column.value);
                try {
                    this.isLoading = true;
                    await this.addSegment(this.segment);
                    this.$router.push({
                        name: 'segment-management',
                        params: {
                            page: this.segment.data.page,
                            category: this.segment.data.category,
                        },
                    });
                } catch (error) {
                    this.isLoading = false;
                    console.error(error);
                    this.$toast.fromError(error, 'Unable to save your grid segment at this time.');
                }
            },
            updateColumns(data) {
                this.draggableColumns = data;
                this.sortableColumns = this.getSortableColumns(this.availableColumns);
                this.selectedSortableColumn = this.sortableColumns[0].value;
            },
            closeColumnsListModal() {
                this.showColumnsListModal = false;
            },
            deleteField(index) {
                this.draggableColumns.splice(index, 1);
                this.sortableColumns = this.getSortableColumns(this.availableColumns);
                this.selectedSortableColumn = this.sortableColumns[0].value;
            },
            setFocus(event) {
                event.to.focus();
            },
            updateSorting(value) {
                this.columnSorting = value;
            },
            updateSegmentSharedWith({scope, userIds}) {
                this.segment.scope = scope;
                this.segment.setUsersFromIds(userIds);
            },
            queryValidated({hasInvalidItems}) {
                this.hasInvalidQuery = hasInvalidItems;
            },
        },
        created() {
            this.fetchData();
        },
    };
</script>
